import { useDisabledPromoCodesList } from '@if/core'
import { isAfter, isWithinInterval } from '@if/core/date'
import { EPaymentFrequency, EPaymentPlan } from '@if/core/types'
import { formatCurrency, formatDate, formatUtcIsoDateTime } from '@if/core/utils'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import StarIcon from '@mui/icons-material/Star'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useMerchantFinancingConfig } from '@src/data/api/configs-api/financing-config-api'
import { useEditCreditApplication } from '@src/data/api/credit-api/credit-api'
import { useGetPersonalFunding } from '@src/data/api/worksheet-api/personal-loan-worksheet-api'
import { getDisplayedMaxAmountFinanced } from '@src/data/creditapp-selectors'
import { ComputeFundingDto } from '@src/types/ComputePersonalLoanFundingDto'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { MerchantPaymentPlan } from '@src/types/MerchantPaymentPlan'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { adjustCreditApplicationYearsForApi } from '../../credit-hooks'

interface Props {
  open: boolean
  onClose: () => void
  creditApp: CreditApplication
  merchant: Merchant
}

interface PlanRowProps {
  plan: MerchantPaymentPlan | null
  label: string
  stars: number | undefined
  disabled: boolean
  computeBase: Omit<ComputeFundingDto, 'paymentPlanId' | 'requestId'> | null
  creditAppId: string
  maxAmount: number
  maxPmt: number
  onApply: (promoCode: string) => void
  isApplying: boolean
}

const PlanRow = ({ plan, label, stars, disabled, computeBase, creditAppId, maxAmount, maxPmt, onApply, isApplying }: PlanRowProps) => {
  const { t } = useTranslation()
  const planId = plan?.id ?? EPaymentPlan.RegularDailyInterests

  const computeDto = useMemo(() => {
    if (!computeBase) return null
    return { ...computeBase, paymentPlanId: planId as string, requestId: crypto.randomUUID() }
  }, [computeBase, planId])

  const [fundingResult, isComputing] = useGetPersonalFunding(creditAppId, computeDto, maxAmount)
  const isValid = fundingResult && fundingResult.paymentForFrequency <= maxPmt
  const term = plan?.loanTerm ?? computeBase?.term

  return (
    <TableRow sx={{ opacity: disabled ? 0.5 : 1 }}>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2">{label}</Typography>
          {stars != null && (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
              ({stars}<StarIcon sx={{ fontSize: 14 }} />{stars < 5 && '+'})
            </Box>
          )}
        </Box>
      </TableCell>
      <TableCell align="right">
        {term && <Typography variant="body2">{term} {t('common.months')}</Typography>}
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2">{plan?.merchantFeeRate != null ? `${plan.merchantFeeRate}%` : '—'}</Typography>
      </TableCell>
      <TableCell align="right">
        {isComputing ? (
          <CircularProgress size={18} />
        ) : fundingResult ? (
          <Typography variant="body2">{formatCurrency(fundingResult.paymentForFrequency)}</Typography>
        ) : null}
      </TableCell>
      <TableCell align="center">
        {!isComputing && fundingResult && (
          isValid
            ? <CheckCircleIcon color="success" fontSize="small" />
            : <ErrorIcon color="error" fontSize="small" />
        )}
      </TableCell>
      <TableCell align="right">
        {!disabled && isValid && (
          <Button
            size="small"
            variant="contained"
            onClick={() => onApply(plan?.promoCode ?? '')}
            disabled={isApplying}
          >
            {t('viewCreditApplication.applyPlan')}
          </Button>
        )}
      </TableCell>
    </TableRow>
  )
}

const PaymentPlanSimulator = ({ open, onClose, creditApp, merchant }: Props) => {
  const { t } = useTranslation()
  const [financingConfig] = useMerchantFinancingConfig(merchant.id, creditApp.financingProgramId)
  const maxAmount =
    getDisplayedMaxAmountFinanced(
      creditApp.financingProgramId,
      creditApp.finalCreditDecision.maxAmountFinanced,
      financingConfig,
    ) ?? 0
  const maxPmt = creditApp.finalCreditDecision.maxPmtAmount
  const maxTerm = creditApp.finalCreditDecision.maxTermDuration ?? 60

  const [amountRequested, setAmountRequested] = useState<number>(creditApp.requestedLoanAmount ?? maxAmount)
  const [editCreditApp, isEditing] = useEditCreditApplication()

  const activePaymentPlans = useMemo(() => {
    if (!merchant?.paymentPlans) return []
    const currentDate = new Date()
    const DISTANT_FUTURE = new Date()
    DISTANT_FUTURE.setFullYear(DISTANT_FUTURE.getFullYear() + 100)
    const currentUtcDateFormatted = new Date(formatUtcIsoDateTime(new Date()))
    return merchant.paymentPlans.filter((plan) => {
      const planActiveFrom = new Date(plan.activeFrom)
      const planExpiresOn = new Date(plan?.expiresOn ?? DISTANT_FUTURE)
      return (
        isWithinInterval(currentDate, { start: planActiveFrom, end: planExpiresOn }) &&
        isAfter(planExpiresOn, currentUtcDateFormatted)
      )
    })
  }, [merchant?.paymentPlans])

  const disabledPromoList = useDisabledPromoCodesList(
    merchant.planPromoRules,
    creditApp.normsCreditDecision,
    creditApp.financingProgramId,
  )

  const promoCodeMinStars = useMemo(() => {
    const map = new Map<string, number>()
    if (!merchant?.planPromoRules) return map
    for (const rule of merchant.planPromoRules) {
      if (!/^\*+$/.test(rule.planName)) continue
      const stars = rule.planName.length
      const codes = [rule.defaultPromoCode, ...(rule.selectablePromoCodes ?? [])]
      for (const code of codes) {
        const current = map.get(code)
        if (current === undefined || stars < current) map.set(code, stars)
      }
    }
    return map
  }, [merchant?.planPromoRules])

  const isAmountValid = amountRequested > 0 && amountRequested <= maxAmount
  const today = formatDate(new Date())
  const firstPaymentOn = formatDate(new Date(new Date().setDate(new Date().getDate() + 30)))

  const computeBase = useMemo(() => {
    if (!isAmountValid) return null
    return {
      creditApplicationId: creditApp.id,
      amountRequested,
      paymentFrequency: EPaymentFrequency.Monthly,
      term: maxTerm,
      includeInsurance: false,
      interestRate: creditApp.finalCreditDecision.interestRate,
      hasCoapplicant: creditApp.coApplicant !== null,
      stateIso: creditApp.applicant.currentAddress.stateIso!,
      deliveryOn: today,
      firstPaymentOn,
      merchantId: creditApp.merchantId,
    }
  }, [isAmountValid, amountRequested, maxTerm, creditApp, today, firstPaymentOn])

  const handleApply = async (promoCode: string) => {
    const data = { ...creditApp, prequalPromoCode: promoCode || null, requestedLoanAmount: amountRequested }
    adjustCreditApplicationYearsForApi(data)
    await editCreditApp(data)
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('viewCreditApplication.paymentPlanSimulator')}</DialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={2} sx={{ mt: 1, mb: 2 }} alignItems="center">
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {t('viewCreditApplication.maxMontlyPayment')}: <strong>{formatCurrency(maxPmt)}</strong>
          </Typography>
          <TextField
            label={t('editCreditApplication.amountRequested')}
            type="number"
            value={amountRequested}
            onChange={(e) => setAmountRequested(Number(e.target.value))}
            error={!isAmountValid}
            helperText={!isAmountValid ? `Max: ${formatCurrency(maxAmount)}` : undefined}
            slotProps={{ htmlInput: { min: 0, max: maxAmount, step: 100 } }}
            size="small"
            fullWidth
          />
        </Stack>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('worksheet.paymentPlan')}</TableCell>
                <TableCell align="right">{t('viewCreditApplication.maxTerm')}</TableCell>
                <TableCell align="right">{t('viewCreditApplication.merchantFee')}</TableCell>
                <TableCell align="right">{t('viewCreditApplication.monthlyPayment')}</TableCell>
                <TableCell align="center" />
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              <PlanRow
                plan={null}
                label={t('common.regularPlan')}
                stars={undefined}
                disabled={false}
                computeBase={computeBase}
                creditAppId={creditApp.id}
                maxAmount={maxAmount}
                maxPmt={maxPmt}
                onApply={handleApply}
                isApplying={isEditing}
              />
              {activePaymentPlans.map((plan) => {
                const promoCode = plan.promoCode.replace('PLAN', '').trim()
                const minStars = promoCodeMinStars.get(plan.promoCode) ?? promoCodeMinStars.get(promoCode)
                const isDisabled = (disabledPromoList ?? new Set<string>()).has(promoCode)
                return (
                  <PlanRow
                    key={plan.id}
                    plan={plan}
                    label={promoCode}
                    stars={minStars}
                    disabled={isDisabled}
                    computeBase={computeBase}
                    creditAppId={creditApp.id}
                    maxAmount={maxAmount}
                    maxPmt={maxPmt}
                    onApply={handleApply}
                    isApplying={isEditing}
                  />
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  )
}

export default PaymentPlanSimulator
